// [T54179 + T54191] Burndown chart under the milestone timeline: remaining
// planned hours per day, from the first day time was clocked on the
// milestone until the projected end. Lines: actual (clocked
// TimeRegistrations — may dip below 0 when more was clocked than planned),
// planned (schedule: each scheduled ticket burns its estimate across its
// window) and target (allocated capacity, "everyone fully productive").
// Recharts LineChart.
import React from 'react';
import { observer } from 'mobx-react';
import { observable } from 'mobx';
import styled from 'styled-components';
import {
    ResponsiveContainer, LineChart, Line, XAxis, YAxis, CartesianGrid,
    Tooltip, Legend, ReferenceLine,
} from 'recharts';
import moment from 'moment';
import { api } from '../../store/Base';

const C = {
    textPrimary: '#1f2d3d',
    textSecondary: '#5a6a7e',
    border: '#e1e4e8',
    accent: '#2185d0',
    success: '#21ba45',
    danger: '#db2828',
    warning: '#f2711c',
    purple: '#a333c8',
    teal: '#00b5ad',
    surface: '#ffffff',
};

export interface BurndownPoint {
    date: string;
    actual: number;
    planned: number;
    target: number;
    not_finished: number;
    not_started: number;
    projection: number | null;
}

interface OnTimeStats {
    done: number;
    on_time: number;
    late: number;
    no_data: number;
}

interface BurndownMeta {
    total_planned_hours: number;
    logged_hours: number;
    projected_finish: string | null;
    capacity_finish: string | null;
    capacity_hours_per_week: number;
    velocity_hours_per_day: number;
    actual_finish: string | null;
    deadline: string | null;
    on_time: OnTimeStats;
}

const Wrapper = styled.div`
    background: ${C.surface};
    padding: 14px 18px 10px;
`;

const Header = styled.div`
    display: flex;
    align-items: baseline;
    gap: 14px;
    margin-bottom: 6px;
    flex-wrap: wrap;
`;

const Title = styled.h3`
    font-size: 14px;
    font-weight: 700;
    color: ${C.textPrimary};
    margin: 0;
`;

const MetaText = styled.span`
    font-size: 12px;
    color: ${C.textSecondary};
`;

const OnTimeChip = styled.span`
    font-size: 12px;
    font-weight: 600;
`;

@observer
export default class BurndownChart extends React.Component<{ milestoneId: number | null; chartHeight?: number }> {
    @observable points: BurndownPoint[] = [];
    @observable meta: BurndownMeta | null = null;
    @observable error: string | null = null;
    @observable loaded = false;

    async load() {
        const mid = this.props.milestoneId;
        if (!mid) return;
        this.error = null;
        try {
            const res: any = await api.get(`subproject/${mid}/burndown/`);
            const data = res?.data ?? res;
            this.points = (data?.points ?? []) as BurndownPoint[];
            this.meta = data as BurndownMeta;
            this.loaded = true;
        } catch (e: any) {
            console.error('burndown fetch failed', e);
            this.error = 'Failed to load burndown data';
        }
    }

    componentDidUpdate(prevProps: any) {
        if (prevProps?.milestoneId !== this.props.milestoneId && this.props.milestoneId) {
            this.load();
        }
    }

    componentDidMount() {
        this.load();
    }

    fmtDate = (d: string) => moment(d).format('D MMM');
    fmtHours = (h: number) => Math.round(h * 10) / 10;

    render() {
        if (this.error) {
            return (
                <Wrapper data-test-burndown-error>
                    <Title>Burndown</Title>
                    <MetaText>{this.error}</MetaText>
                </Wrapper>
            );
        }
        if (!this.loaded) return null;

        const m = this.meta;
        const todayStr = new Date().toISOString().slice(0, 10);

        return (
            <Wrapper data-test-burndown>
                <Header>
                    <Title>Burndown — remaining hours</Title>
                    <MetaText>
                        {m && `${this.fmtHours(m.total_planned_hours)}h planned · ${this.fmtHours(m.logged_hours)}h clocked`}
                    </MetaText>
                    {/* On-time: done tickets finished (last clocked day) on or
                        before their scheduled end / the deadline. */}
                    {m && m.on_time && m.on_time.done > 0 && (
                        <OnTimeChip
                            data-test-burndown-ontime
                            style={{ color: m.on_time.late > 0 ? C.danger : C.success }}
                        >
                            Done on time {m.on_time.on_time}/{m.on_time.done}
                            {m.on_time.no_data > 0 && ` · ${m.on_time.no_data} never clocked`}
                        </OnTimeChip>
                    )}
                    {m && m.capacity_hours_per_week > 0 && (
                        <MetaText>
                            capacity {this.fmtHours(m.capacity_hours_per_week)}h/wk
                            {m.capacity_finish && ` · done by ${moment(m.capacity_finish).format('D MMM YYYY')}`}
                        </MetaText>
                    )}
                    {m && m.velocity_hours_per_day > 0 && m.actual_finish && (
                        <MetaText>
                            pace {this.fmtHours(m.velocity_hours_per_day)}h/day · runs out {moment(m.actual_finish).format('D MMM YYYY')}
                        </MetaText>
                    )}
                </Header>
                {/* [T54191] Resizable from the milestone detail screen via the
                    drag handle above the chart. */}
                <ResponsiveContainer width="100%" height={this.props.chartHeight ?? 220}>
                    <LineChart data={this.points as any} margin={{ top: 5, right: 24, bottom: 0, left: 0 }}>
                        <CartesianGrid strokeDasharray="3 3" stroke={C.border} />
                        <XAxis
                            dataKey="date"
                            tickFormatter={this.fmtDate}
                            stroke={C.textSecondary}
                            tick={{ fontSize: 11 }}
                            interval="preserveStartEnd"
                        />
                        <YAxis
                            stroke={C.textSecondary}
                            tick={{ fontSize: 11 }}
                            width={42}
                        />
                        <Tooltip
                            labelFormatter={(d: any) => moment(d).format('ddd D MMM YYYY')}
                            formatter={(value: any, name: any) => [value + 'h', name]}
                        />
                        <Legend wrapperStyle={{ fontSize: 12 }} />
                        <ReferenceLine y={0} stroke="#b0b8c1" />
                        <ReferenceLine
                            x={todayStr}
                            stroke={C.accent}
                            strokeDasharray="4 4"
                            label={{ value: 'Today', position: 'insideTopLeft', fontSize: 10, fill: C.accent }}
                        />
                        {m && m.deadline && (
                            <ReferenceLine
                                x={m.deadline}
                                stroke={C.danger}
                                strokeDasharray="4 4"
                                label={{ value: 'Deadline', position: 'insideTopRight', fontSize: 10, fill: C.danger }}
                            />
                        )}
                        <Line
                            type="monotone"
                            dataKey="planned"
                            name="Planned (schedule)"
                            stroke={C.accent}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="actual"
                            name="Clocked (actual)"
                            stroke={C.success}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                        {/* Dotted continuation of the actual line from today
                            at the historical working-day pace; only drawn
                            while the actual line is above 0. */}
                        <Line
                            type="monotone"
                            dataKey="projection"
                            name="Projection (pace)"
                            stroke={C.success}
                            strokeWidth={1.5}
                            strokeDasharray="2 4"
                            dot={false}
                            connectNulls={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="monotone"
                            dataKey="target"
                            name="Target (capacity)"
                            stroke={C.warning}
                            strokeWidth={2}
                            strokeDasharray="6 3"
                            dot={false}
                            isAnimationActive={false}
                        />
                        {/* Ticket-level lifecycle: remaining hours of tickets
                            not finished yet / not started yet — step down on
                            each ticket's done_at / started_at. */}
                        <Line
                            type="stepAfter"
                            dataKey="not_finished"
                            name="Not finished yet"
                            stroke={C.purple}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                        <Line
                            type="stepAfter"
                            dataKey="not_started"
                            name="Not started yet"
                            stroke={C.teal}
                            strokeWidth={2}
                            dot={false}
                            isAnimationActive={false}
                        />
                    </LineChart>
                </ResponsiveContainer>
            </Wrapper>
        );
    }
}
